import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Account } from "../../../../models/account";
import { Controller, FieldValues, Path, UseFormReturn, FieldName, FieldErrors } from "react-hook-form";
import { DocumentStructure } from "../../../../models/document-structure";
import { ErrorMessage, FieldValuesFromFieldErrors } from "@hookform/error-message"
import { CssField } from "../../MuiInput.styled";
import { getValueByPath } from "../../../../helpers/helpers";
import { Car } from "../../../../models/car";
import { getCars } from "../../../../services/carsService";
import { AccountStructure } from "../../../../models/accounts-structure";

interface CarSearchProps<T extends FieldValues> {
    fieldStructure: AccountStructure | DocumentStructure, // field data
    useForm: UseFormReturn<T>; // form
    onSelect: (object: Car) => void, // update the form after object selected
    prefixPath?: string, // prefix path for the field in the form
    disabled?: boolean, // disable the field
}

function CarSearch<T extends FieldValues>({ fieldStructure, useForm, onSelect, prefixPath, disabled}: CarSearchProps<T>) {

    
    const formPath = (prefixPath? prefixPath + "." : "" ) + fieldStructure.field_name;

    const [options, setOptions] = useState<Car[]>([]);
    const [inputValue, setInputValue] = useState("");
    

    useEffect(() => {

        if (inputValue.length < 3) {
            setOptions([]);
            return;
        }

        let active = true;

        (async () => {
            try {

                const path = fieldStructure.field_name;
                const cars = await searchCars(path as Path<T>, inputValue);

                if (active) {
                    setOptions(cars);
                }
            } catch (e) {
               console.error(e);
            }
        })();

        return () => {
            active = false;
        };

    }, [inputValue]);


    /**
     * search cars by key and value
     * @param key - account key name 
     * @param value - value for search
     * @returns cars
     */
    async function searchCars(key: Path<T>, value: string): Promise<Car[]>{

        const body = {
            [key]: value
        }

        const cars: Car[] = await getCars(body, 'id', "5", 'ASC', ["customData","customer.customData"]);

        return cars;
        
    }


    /**
     * update the form with a new value
     * @param val - the new value, can be a specific field value or the whole object itself
     */
    function onChange(val: string | Car | null): void {

        if (typeof val === "string") {
            useForm.setValue(formPath as  Path<T>, val as any);
            return;
        }

        if (val === null) {
            useForm.setValue(formPath as  Path<T>, null as any); // or reset relevant fields
            return;
        }

        onSelect(val);
    }


    function renderOption(option: Car): string {

        if(fieldStructure.field_name === "license_number"){
            return String(getValueByPath(option, fieldStructure.field_name as keyof Car));
        }

        return String(option["license_number" as keyof Car]+ ": " + String(getValueByPath(option, fieldStructure.field_name as keyof Car)));

    }


    function getValueByFieldName(car: Car): string {
        const path = fieldStructure.field_name;
        const value = getValueByPath(car, path)
        return String(value);
    }

    return (
        <CssField sx={{ minWidth: 200 }} error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}>
            <Controller
                name={formPath as Path<T>}
                control={useForm.control}
                render={({ field }) => (
                    <Autocomplete<Car, false, false, true>
                    {...field}
                    freeSolo
                    clearOnBlur={false}
                    options={options}
                    value={null}
                    getOptionLabel={(option) =>typeof option === "string" ? option : getValueByFieldName(option)}
                    inputValue={String(field.value ?? "")}
                    onChange={(_, newValue)=> onChange(newValue)}
                    disabled={disabled}

                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {renderOption(option)}
                        </li>
                    )}

                    onInputChange={(_, newInput) => {
                        field.onChange(newInput);
                        setInputValue(newInput);
                    }}

                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={fieldStructure.field_display_name}
                            error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}
                            helperText={<ErrorMessage errors={useForm.formState.errors} name={fieldStructure.field_name as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} />}
                        />
                    )}
                    />
                )}
            />
        </CssField>
        
    );
}

export default CarSearch;